'use client';

import { useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { searchMattersAction } from '@/lib/services/search/actions';
import { SEARCH_RESULTS_LIMIT } from '@/lib/constants';
import {
  GenericContact,
  type Matter,
  type MatterSummary
} from '@/lib/services/matters/types';
import {
  calculateStageProgress,
  transformStagesToListCardFormat,
  type ListCardStage
} from '@/lib/services/tasks/types';

export interface MatterSearchResult {
  id: number;
  isEstateAdmin: boolean;
  reference: string;
  description?: string;
  type?: string; // Matter type slug (e.g., "estate-administration")
  resultTitle: string;
  executorName: string;
  /** Full LPR name(s), comma-joined — matches the matter-list row (LF-4960). */
  lprNames?: string;
  /** PMS matter-type label, shown for non-estate matters instead of the client list (LF-4960). */
  matterTypeLabel?: string;
  /** Stage-completion progress, mirroring the matter list's progress dots (LF-4960). Undefined when the matter has no persisted stages. */
  progress?: {
    total: number;
    completed: number;
    stages?: ListCardStage[];
  };
  allContacts: string[];
  clientNames?: string[];
  clients?: Array<{
    id: number;
    firstName?: string;
    lastName?: string;
    organisationName?: string | null;
  }>;
  lastUpdated: string;
  isArchived?: boolean;
}

export function transformMatterToSearchResult(
  matter: MatterSummary | Matter
): MatterSearchResult {
  const isEstateAdmin = matter.type === 'estate-administration';
  const description = matter?.metadata?.description || '';
  const decedentName = [
    matter.decedentFirstName,
    matter.decedentMiddleName,
    matter.decedentLastName
  ]
    .filter(Boolean)
    .join(' ');
  const rawBeneficiaries =
    ('beneficiaries' in matter ? matter.beneficiaries : undefined) || [];
  const beneficiaries = rawBeneficiaries.map(
    (contact: GenericContact) => `${contact.firstName} ${contact.lastName}`
  );
  const lprs = (matter.legalPersonalRepresentatives || []).map((contact) =>
    [contact.firstName, contact.middleName, contact.lastName]
      .filter(Boolean)
      .join(' ')
  );
  const contacts = [decedentName, ...beneficiaries, ...lprs].filter(
    (name): name is string => Boolean(name)
  );

  // Client names - prefer clients array (new contact-based), fallback to metadata.clientNames (legacy)
  const clientNames =
    matter.clients && matter.clients.length > 0
      ? matter.clients.map(
          (c) =>
            c.organisationName ||
            [c.firstName, c.lastName].filter(Boolean).join(' ') ||
            `Client #${c.id}`
        )
      : matter?.metadata?.clientNames || [];

  const progress =
    isEstateAdmin && matter.stages && matter.stages.length > 0
      ? {
          ...calculateStageProgress(matter.stages),
          stages: transformStagesToListCardFormat(matter.stages)
        }
      : undefined;

  const lastUpdated =
    'lastUpdated' in matter ? matter.lastUpdated : matter.updatedAt;
  const matterTypeLabel =
    matter.metadata && 'pms' in matter.metadata
      ? matter.metadata.pms?.matterTypeLabel
      : undefined;

  return {
    id: matter.id,
    isEstateAdmin,
    reference: matter.lawFirmInternalReference || `M-${matter.id}`,
    description,
    type: matter.type,
    clientNames,
    clients: matter.clients,
    allContacts: contacts,
    resultTitle: isEstateAdmin ? `Estate of ${decedentName}` : `${description}`,
    executorName: lprs?.[0] || '',
    lprNames: lprs.filter(Boolean).join(', '),
    matterTypeLabel,
    progress,
    lastUpdated,
    isArchived: matter.isArchived || false
  };
}

export function useSearch(query: string) {
  const debouncedQuery = useDebouncedValue(query, 300);
  const enabled = debouncedQuery.length >= 2;

  const { data, isFetching } = useQuery<MatterSearchResult[]>({
    queryKey: ['matter-search', debouncedQuery],
    enabled,
    queryFn: async () => {
      const result = await searchMattersAction(debouncedQuery);
      if (!result.success || !result.data?.matters) return [];
      return result.data.matters
        .map(transformMatterToSearchResult)
        .slice(0, SEARCH_RESULTS_LIMIT);
    }
  });

  return {
    matters: enabled ? (data ?? []) : [],
    isLoading: enabled && isFetching
  };
}
